/**
 * Which conversation does the next run belong to?
 *
 * ── WHY THIS IS A MODULE AND NOT A `||` CHAIN AT THE CALL SITE ───────────────────────────────
 *
 * It was a `||` chain, in three places:
 *
 *     sessionId || parentRunId || readPersistedMultiAgentSession().sessionId || null
 *
 * Three sources, all of them the LEGACY store or its localStorage key, and each copy free to drift
 * from the others — the send path, the resume path and the execute-plan path each had their own.
 * Getting it wrong does not throw: the run is created, it streams, it answers. It is simply filed
 * under the wrong conversation, and the reader finds out later when their history is wrong.
 *
 * As a function the precedence can be asserted over the whole matrix of inputs, including the states
 * that only occur for a few hundred milliseconds during a send.
 *
 * ── THE THREE IDENTITIES, WHICH ARE NOT INTERCHANGEABLE ──────────────────────────────────────
 *
 *   · sessionId — the CONVERSATION. Stable across every run in it. What this resolves.
 *   · runId     — ONE execution. New for every question, including follow-ups.
 *   · intent    — what was asked to start a run. Immutable once the run exists.
 *
 * They get conflated because of one real convention: the first run of a brand-new conversation is
 * created with no `session_id`, and the client then treats that run's id AS the session id until the
 * server names one. That is the only place a run id may stand in for a session id, and it is spelled
 * out below rather than left as a bare `|| parentRunId`.
 */

/** What `weaveSessionStore` knows — the owner of the active conversation. */
export interface WeaveIdentity {
  /** The adopted or persisted conversation. */
  sessionId: string | null;
  /** The run this client started, and the conversation it was created in. */
  current: { runId: string; sessionId: string | null } | null;
}

/**
 * What the legacy store and its localStorage key know.
 *
 * Still consulted, and deliberately so: a run restored by legacy hydration fills `multiAgentStore`
 * and not `weaveSessionStore`, so dropping this would make a follow-up on such a run open a NEW
 * conversation. It is the last fallback, never the first answer.
 */
export interface LegacyIdentity {
  sessionId: string | null;
  runId: string | null;
  persistedSessionId: string | null;
}

/**
 * The conversation the next run belongs to, or `null` for "start a new one".
 *
 * Precedence, most authoritative first:
 *
 *   1. the live run's own conversation — `weaveSessionStore` recorded it when the run was created,
 *      in the same update as the run id, so it cannot lag behind the run it describes;
 *   2. the adopted conversation — the reader opened it from the drawer or restored it on load;
 *   3. the live run's id, ONLY when it has no session yet — the brand-new-chat convention above;
 *   4. the legacy store, then its persisted key — for runs this client did not start.
 *
 * Rule 2 sits above rule 3 on purpose. A reader who opens conversation B and then asks a question
 * must land in B, and rule 3 would answer with whatever run happened to be live when they clicked.
 */
export function activeConversationId(weave: WeaveIdentity, legacy: LegacyIdentity): string | null {
  return (
    weave.current?.sessionId ||
    weave.sessionId ||
    weave.current?.runId ||
    legacy.sessionId ||
    legacy.runId ||
    legacy.persistedSessionId ||
    null
  );
}

/**
 * The conversation a just-created run belongs to.
 *
 * Called immediately after `createRun` with the id that was SENT and the one the server answered
 * with. The client's own id wins when it had one — it is what the request asked for, and the server
 * echoes a session for a run it has just filed. When the client had none, this is the moment the
 * conversation gets its identity, from the server if it named one and from the run id otherwise.
 *
 * Never returns null: a created run always belongs to some conversation, and returning null here
 * would leave the next follow-up unable to find it.
 */
export function conversationOfCreatedRun(
  sentSessionId: string | null,
  created: { run_id: string; session_id?: string | null },
): string {
  return sentSessionId || created.session_id || created.run_id;
}

/**
 * Is the run being hydrated part of the conversation the reader is already in?
 *
 * ── WHAT `preserveSession` ACTUALLY ENCODES ──────────────────────────────────────────────────
 *
 * Not "the active conversation" — a SAME-CONVERSATION PREDICATE. `hydrateFromDetail` uses it to
 * decide two things at once:
 *
 *   · the conversation id — keep the one on screen, or adopt the hydrated run's;
 *   · the thread — MERGE the local turns over the server's (they carry richer activity and
 *     reasoning that `session_runs` does not), or REPLACE them wholesale.
 *
 * Both hang on the same question: is this navigation inside one conversation (opening an older run
 * of the chat I am in, or refreshing back into it), or a switch to a different one? Answer it wrong
 * in the "same" direction and one conversation's turns are stitched under another's. Answer it wrong
 * in the "different" direction and a refresh silently discards the richer local turns.
 *
 * WHY IT TAKES THE CONVERSATION AS AN ARGUMENT rather than reading a store: the caller must resolve
 * it BEFORE adopting the new one. `weaveSessionStore.adopt()` moves the active conversation, so a
 * predicate that read the store afterwards would always answer "same" — and the merge-versus-replace
 * decision would never fire.
 */
export function isSameConversation(
  activeConversationId: string | null,
  hydratedRunSessionId: string | null | undefined,
): boolean {
  return Boolean(activeConversationId && hydratedRunSessionId && activeConversationId === hydratedRunSessionId);
}

/**
 * Which conversation a page load should try to restore, and from which run.
 *
 * `restoreTarget` takes this shape and ranks it against the URL and the run list; this only decides
 * WHOSE persisted identity it gets. `weaveSessionStore` persists both halves and is the owner, so it
 * answers first; the legacy `ma:session` key remains as the fallback for a browser that has one but
 * has not yet run anything through the v1 store.
 *
 * The two halves are kept separate on purpose — a session survives its runs, so restoring the right
 * conversation and restoring the right run are different questions, and `restoreTarget` ranks them
 * differently.
 */
export function persistedRestoreIdentity(
  weave: WeaveIdentity,
  legacyPersisted: { runId: string | null; sessionId: string | null },
): { runId: string | null; sessionId: string | null } {
  return {
    sessionId: weave.sessionId || weave.current?.sessionId || legacyPersisted.sessionId || null,
    runId: weave.current?.runId || legacyPersisted.runId || null,
  };
}

/**
 * A new, CLIENT-OWNED conversation id — for a lane-v2 conversation, whose id must exist before the
 * first turn (the stream ticket is minted for it first, so the first turn streams live). A UUID, the
 * only shape MSA's session routes accept. The legacy lane keeps its own convention (the first run's
 * id stands in until the server names one — see `activeConversationId`).
 */
export function newConversationId(random: () => string = () => crypto.randomUUID()): string {
  return random();
}
